/**
 * Risk-assessment record schema — DataTable columns + FormModal
 * fields for Step 6 (`risk_assessment_agent`).
 *
 * Real DB shape (newer rows accumulate fields older sessions didn't
 * emit — extractor tolerates both):
 *
 *   { risk_id, asset_id, threat_id,
 *     damage_id / damage_scenario_id,   // alias on older rows
 *     attack_path_id,                    // newer rows
 *     risk_value (1-5),
 *     risk_level ('Low' / 'Medium' / 'High'),
 *     impact_rating (from Step 3),
 *     attack_feasibility_rating (from Step 5),
 *     treatment_decision ('Avoid'/'Reduce'/'Share'/'Retain'),
 *     treatment_priority ('Immediate'/'High'/'Medium'/'Low'),
 *     treatment_justification,
 *     // hierarchical (when source === 'child_tara'):
 *     source, source_session_id, is_relevant, relevance_justification,
 *     risk_amplification, amplification_rationale, cumulative_factors }
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import {
  getImpactColor,
  getFeasibilityColor,
  getRiskValueColor,
  getTreatmentDecisionColor,
  getTreatmentPriorityColor,
} from '../../../../utils/severityStyle';

export interface RiskRecord {
  risk_id: string;
  asset_id?: string;
  threat_id?: string;
  damage_id?: string;
  damage_scenario_id?: string;
  attack_path_id?: string;
  risk_value?: number;
  risk_level?: string;
  impact_rating?: string;
  attack_feasibility_rating?: string;
  treatment_decision?: string;
  treatment_priority?: string;
  treatment_justification?: string;
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

/** Older sessions store the damage reference as `damage_id`; newer
 *  ones use `damage_scenario_id`. Resolve a single human-readable
 *  string for the table column. */
function resolveDamageRef(row: RiskRecord): string {
  return String(row.damage_scenario_id ?? row.damage_id ?? '');
}

export const TREATMENT_DECISIONS = [
  { value: 'Avoid', label: 'Avoid' },
  { value: 'Reduce', label: 'Reduce' },
  { value: 'Share', label: 'Share' },
  { value: 'Retain', label: 'Retain' },
];

export const TREATMENT_PRIORITIES = [
  { value: 'Immediate', label: 'Immediate' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

export const RISK_VALUE_OPTIONS = [
  { value: 1, label: '1 — Very Low' },
  { value: 2, label: '2 — Low' },
  { value: 3, label: '3 — Medium' },
  { value: 4, label: '4 — High' },
  { value: 5, label: '5 — Critical' },
];

export const RISK_LEVELS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

const renderRiskValue = (row: RiskRecord) => {
  const v = row.risk_value;
  if (v == null) return '';
  return <LevelBadge color={getRiskValueColor(v) ?? 'var(--color-status-neutral)'} label={String(v)} size="sm" />;
};

const renderTreatmentDecision = (row: RiskRecord) =>
  row.treatment_decision ? (
    <LevelBadge
      color={getTreatmentDecisionColor(row.treatment_decision)}
      label={String(row.treatment_decision)}
      size="sm"
    />
  ) : (
    ''
  );

const renderTreatmentPriority = (row: RiskRecord) =>
  row.treatment_priority ? (
    <LevelBadge
      color={getTreatmentPriorityColor(row.treatment_priority)}
      label={String(row.treatment_priority)}
      size="sm"
    />
  ) : (
    ''
  );

const renderDamageRef = (row: RiskRecord) => resolveDamageRef(row);

// Impact / Feasibility — the two drivers of every risk value (AUMOVIO
// TC-052). Values are DERIVED by the Step 6 dispatcher from the Step 3
// damage scenario (final_impact_rating) and the Step 5 attack paths
// (strongest attack_feasibility_rating for the threat), so they track
// cascade recalculations rather than trusting the risk row's own copy.
const renderImpact = (row: RiskRecord) =>
  row.impact_rating ? (
    <LevelBadge
      color={getImpactColor(String(row.impact_rating), { scheme: 'fourTier' })}
      label={String(row.impact_rating)}
      size="sm"
    />
  ) : (
    ''
  );

const renderFeasibility = (row: RiskRecord) =>
  row.attack_feasibility_rating ? (
    <LevelBadge
      color={getFeasibilityColor(String(row.attack_feasibility_rating))}
      label={String(row.attack_feasibility_rating)}
      size="sm"
    />
  ) : (
    ''
  );

/**
 * Visible table columns. `attack_path_id` and `risk_level` stay off the
 * table (sparse cross-step inherits) but remain on the `RiskRecord`
 * type + `RISK_FORM_FIELDS` so consumers can still read them
 * programmatically and users can edit them manually via the FormModal.
 * `impact_rating` / `attack_feasibility_rating` are shown — the
 * dispatcher derives them from Steps 3/5 before the table renders.
 *
 * Column widths sum to 100%. Edit mode reserves 4% for `_actions`,
 * so EDIT widths sum to 96%.
 */
export const RISK_COLUMNS_READ = [
  { key: 'risk_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'asset_id', label: 'Asset', sortable: true, width: '9%' },
  { key: 'threat_id', label: 'Threat', sortable: true, width: '9%' },
  {
    key: 'damage_ref',
    label: 'Damage',
    sortable: false,
    width: '9%',
    render: renderDamageRef,
  },
  {
    key: 'impact_rating',
    label: 'Impact',
    sortable: true,
    width: '8%',
    render: renderImpact,
  },
  {
    key: 'attack_feasibility_rating',
    label: 'Feasibility',
    sortable: true,
    width: '8%',
    render: renderFeasibility,
  },
  {
    key: 'risk_value',
    label: 'Value',
    sortable: true,
    width: '6%',
    render: renderRiskValue,
  },
  {
    key: 'treatment_decision',
    label: 'Treatment',
    sortable: true,
    width: '10%',
    render: renderTreatmentDecision,
  },
  {
    key: 'treatment_priority',
    label: 'Priority',
    sortable: true,
    width: '10%',
    render: renderTreatmentPriority,
  },
  { key: 'treatment_justification', label: 'Justification', sortable: false, width: '24%' },
];

export const RISK_COLUMNS_EDIT = [
  { key: 'risk_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'asset_id', label: 'Asset', sortable: true, width: '9%' },
  { key: 'threat_id', label: 'Threat', sortable: true, width: '9%' },
  {
    key: 'damage_ref',
    label: 'Damage',
    sortable: false,
    width: '9%',
    render: renderDamageRef,
  },
  {
    key: 'impact_rating',
    label: 'Impact',
    sortable: true,
    width: '8%',
    render: renderImpact,
  },
  {
    key: 'attack_feasibility_rating',
    label: 'Feasibility',
    sortable: true,
    width: '8%',
    render: renderFeasibility,
  },
  {
    key: 'risk_value',
    label: 'Value',
    sortable: true,
    width: '6%',
    render: renderRiskValue,
  },
  {
    key: 'treatment_decision',
    label: 'Treatment',
    sortable: true,
    width: '10%',
    render: renderTreatmentDecision,
  },
  {
    key: 'treatment_priority',
    label: 'Priority',
    sortable: true,
    width: '10%',
    render: renderTreatmentPriority,
  },
  { key: 'treatment_justification', label: 'Justification', sortable: false, width: '20%' },
];

export const RISK_ACTIONS_COLUMN_WIDTH = '4%';

const RISK_ID_PATTERN = /^[A-Z0-9_-]+$/;

/** FormModal field config. `asset_id` / `threat_id` / `damage_id` /
 *  `attack_path_id` options come from sibling-agent fetches (Steps
 *  2-5) injected by the form modal at render time. */
export const RISK_FORM_FIELDS = [
  {
    name: 'risk_id',
    label: 'Risk ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. RISK_001',
    helpText: 'Unique identifier — uppercase letters, numbers, underscores, hyphens.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Risk ID is required';
      if (!RISK_ID_PATTERN.test(s)) {
        return 'Risk ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'risk_value',
    label: 'Risk Value (1-5)',
    type: 'select',
    required: true,
    options: RISK_VALUE_OPTIONS,
    helpText: 'Position on the ISO 21434 risk matrix.',
    width: 'half',
  },
  {
    name: 'asset_id',
    label: 'Affected Asset',
    type: 'select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    width: 'half',
  },
  {
    name: 'threat_id',
    label: 'Related Threat',
    type: 'select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    width: 'half',
  },
  {
    name: 'damage_id',
    label: 'Damage Scenario',
    type: 'select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    width: 'half',
  },
  {
    name: 'attack_path_id',
    label: 'Attack Path',
    type: 'select',
    required: false,
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'Optional — link to a specific feasibility analysis.',
    width: 'half',
  },
  {
    name: 'risk_level',
    label: 'Risk Level',
    type: 'select',
    required: false,
    options: RISK_LEVELS,
    helpText: 'Optional — derivable from risk value but the agent may set it explicitly.',
    width: 'half',
  },
  {
    name: 'treatment_decision',
    label: 'Treatment Decision',
    type: 'select',
    required: true,
    options: TREATMENT_DECISIONS,
    helpText: 'Per ISO 21434 Clause 9.3.4.',
    width: 'half',
  },
  {
    name: 'treatment_priority',
    label: 'Treatment Priority',
    type: 'select',
    required: true,
    options: TREATMENT_PRIORITIES,
    width: 'half',
  },
  {
    name: 'treatment_justification',
    label: 'Treatment Justification',
    type: 'textarea',
    required: true,
    rows: 4,
    placeholder: 'Explain the chosen treatment and its priority.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Justification must be at least 20 characters.';
      return null;
    },
  },
];
